import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Info, X } from "lucide-react";
import { useMainStore } from "@/stores";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import CustomTable from "@/shared/components/ui/Button/CustomTable";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import SimilarAccountActionButton from "./similarAccounts/SimilarAccountActionButton";
import { useCreateAccountFromContacts } from "@/features/accounts/hooks/useAccounts";
import {
  getEngagementStatusVariant,
  getTextInCapitalize,
} from "@/shared/utils/helperFunctions";
import {
  getAccountProfilePath,
  formatFollowUpDate,
  formatFetchDate,
  formatLastSyncedAt,
  companyDomainHref,
  getFollowUpTypeLabel,
  normalizeTopEngagedSignalCounts,
  safeFormatTimeAgo,
  sanitizeCompanyLogoUrl,
  summarizeChannelMetricsLine,
  summarizeTopEngagedScoreBreakdown,
  topEngagedChannelTitle,
} from "@/features/dashboard/utils/accountsDashboardUtils";
import {
  DORMANT_COLUMNS,
  FRESH_INTENT_COLUMNS,
  normalizeDormant,
  normalizeFreshIntent,
  normalizeTopEngaged,
  TOP_ENGAGED_COLUMNS,
  CHANNEL_ICONS,
  FOLLOW_UP_COLUMNS,
  SIMILAR_ACCOUNT_COLUMNS,
  normalizeFollowUps,
  normalizeSimilarAccounts,
} from "@/shared/utils/dashboardConfig";

const TABLE_CHANNEL_KEYS = ["email", "linkedin", "whatsapp"];

const ROWS_PER_PAGE = 25;

const AccountsDashboardSeeAllModal = ({
  isOpen,
  onClose,
  variant,
  title,
  items,
  description,
  referenceAccounts = [],
  lastSyncedAt = null,
}) => {
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const user = useMainStore((state) => state.user);
  const { mutate: createAccount, isPending: isCreatingAccount } =
    useCreateAccountFromContacts();

  const { columns, rows } = useMemo(() => {
    if (variant === "top_engaged") {
      return {
        columns: TOP_ENGAGED_COLUMNS,
        rows: normalizeTopEngaged(items),
      };
    }
    if (variant === "fresh_intent") {
      return {
        columns: FRESH_INTENT_COLUMNS,
        rows: normalizeFreshIntent(items),
      };
    }
    if (variant === "similar") {
      return {
        columns: SIMILAR_ACCOUNT_COLUMNS,
        rows: normalizeSimilarAccounts(items),
      };
    }
    if (variant === "follow_up") {
      return {
        columns: FOLLOW_UP_COLUMNS,
        rows: normalizeFollowUps(items),
      };
    }
    return {
      columns: DORMANT_COLUMNS,
      rows: normalizeDormant(items),
    };
  }, [variant, items]);

  const totalCount = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen, variant, items]);

  const paginatedRows = useMemo(() => {
    if (!rows) return [];
    const start = (page - 1) * ROWS_PER_PAGE;
    return rows.slice(start, start + ROWS_PER_PAGE);
  }, [rows, page]);

  const renderCell = useCallback((item, columnKey) => {
    switch (columnKey) {
      case "company_name": {
        const rawAccount = item._raw;
        const canCreateAccount = Boolean(
          variant === "top_engaged" &&
          !rawAccount?.account_id &&
          rawAccount?.can_create_account &&
          rawAccount?.contact_ids?.length,
        );
        return (
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <CommonAvatar
                imageUrl={sanitizeCompanyLogoUrl(item.company_logo)}
                name={item.company_name}
                className="rounded-full object-cover min-w-8 min-h-8 w-8 h-8 shrink-0"
                size="md"
              />
              <span className="text-xs text-gray-800 dark:text-gray-100 font-medium whitespace-normal break-words">
                {item.company_name}
              </span>
            </div>
            {canCreateAccount ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  if (!user?.id) return;
                  createAccount({
                    userId: user.id,
                    contactIds: rawAccount.contact_ids,
                  });
                }}
                disabled={isCreatingAccount}
                className="shrink-0 text-[0.68rem] font-medium text-secondary hover:text-secondary/80 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCreatingAccount ? "Creating..." : "Create"}
              </button>
            ) : null}
          </div>
        );
      }
      case "actions":
        return <SimilarAccountActionButton account={item._raw} />;
      case "signal_count":
        return (
          <span className="text-xs text-gray-800 dark:text-gray-100 tabular-nums">
            {item.signal_count}
          </span>
        );
      case "reference_score": {
        const score = Number(item.reference_score);
        return (
          <span className="text-xs text-gray-800 dark:text-gray-100 tabular-nums font-medium">
            {Number.isFinite(score) ? score.toFixed(2) : "—"}
          </span>
        );
      }
      case "engagement_temperature":
        return (
          <StatusChip
            label={getTextInCapitalize(item.engagement_temperature)}
            variant={getEngagementStatusVariant(item.engagement_temperature)}
          />
        );
      case "account_status": {
        const isReactivation = item.account_status === "REACTIVATION_OPPORTUNITY";
        return (
          <StatusChip
            label={isReactivation ? "Reactivation opportunity" : "Dormant"}
            variant={isReactivation ? "warning" : "danger"}
          />
        );
      }
      case "signals_by_channel": {
        const raw = item._raw;
        const n = normalizeTopEngagedSignalCounts(raw?.signal_counts);
        return (
          <div className="flex items-center justify-center gap-1 min-w-0">
            {TABLE_CHANNEL_KEYS.map((key) => {
              const Icon = CHANNEL_ICONS[key];
              const count = n[key] ?? 0;
              const active = count > 0;
              if (!Icon) return null;
              return (
                <span
                  key={key}
                  title={`${topEngagedChannelTitle(key)}: ${count}`}
                  className={`inline-flex items-center gap-0.5 rounded-md border px-1 py-0.5 shrink-0 ${active
                    ? "border-secondary/25 bg-secondary/5 text-gray-800 dark:text-gray-100"
                    : "border-gray-100 dark:border-gray-800 bg-gray-50/90 dark:bg-gray-800/90 text-gray-400 dark:text-gray-500"
                    }`}
                >
                  <Icon
                    size={11}
                    strokeWidth={2}
                    className={active ? "text-secondary dark:text-secondary-light" : "text-gray-400 dark:text-gray-500"}
                    aria-hidden
                  />
                  <span className="text-[0.65rem] font-semibold tabular-nums leading-none">
                    {count}
                  </span>
                </span>
              );
            })}
          </div>
        );
      }
      case "outreach_summary": {
        const cm = item._raw?.channel_metrics;
        const parts = ["email", "linkedin", "whatsapp"]
          .map((k) => {
            const line = summarizeChannelMetricsLine(cm, k);
            return line ? `${topEngagedChannelTitle(k)}: ${line}` : null;
          })
          .filter(Boolean);
        const full = parts.length ? parts.join(" · ") : "—";
        return (
          <span
            className="text-xs text-gray-600 dark:text-gray-300 whitespace-normal break-words"
            title={full}
          >
            {full}
          </span>
        );
      }
      case "score_breakdown": {
        const text = summarizeTopEngagedScoreBreakdown(item._raw?.score_breakdown) || "—";
        return (
          <span
            className="text-xs text-gray-600 dark:text-gray-300 whitespace-normal break-words"
            title={text}
          >
            {text}
          </span>
        );
      }
      case "campaigns_summary": {
        const raw = item._raw;
        const campaigns = Array.isArray(raw?.campaigns) ? raw.campaigns : [];
        const bits = [];
        if (typeof raw?.running_campaigns === "number") {
          bits.push(`${raw.running_campaigns} running`);
        }
        if (campaigns.length) {
          const names = campaigns
            .map((c) => c.name)
            .filter(Boolean)
            .slice(0, 2)
            .join(", ");
          const more = campaigns.length > 2 ? ` +${campaigns.length - 2}` : "";
          bits.push(`${names}${more}`);
        }
        const title = campaigns
          .map((c) => c.name)
          .filter(Boolean)
          .join(", ");
        const text = bits.length ? bits.join(" · ") : "—";
        return (
          <span
            className="text-xs text-gray-600 dark:text-gray-300 whitespace-normal break-words"
            title={title || text}
          >
            {text}
          </span>
        );
      }
      case "last_signal_at":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {item.last_signal_at ? safeFormatTimeAgo(item.last_signal_at) : "—"}
          </span>
        );
      case "last_fetched_at":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {formatFetchDate(item.last_fetched_at)}
          </span>
        );
      case "signal_label":
        return (
          <span className="text-xs text-gray-700 dark:text-gray-200 whitespace-normal break-words">
            {item.signal_label}
          </span>
        );
      case "channel":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
            {item.channel}
          </span>
        );
      case "follow_up_type":
        return (
          <span className="text-xs text-gray-700 dark:text-gray-200 whitespace-normal break-words">
            {getFollowUpTypeLabel(item.follow_up_type)}
          </span>
        );
      case "priority": {
        const priority = Number(item.priority) || 3;
        return (
          <StatusChip
            label={priority === 1 ? "High" : priority === 2 ? "Priority" : "Planned"}
            variant={priority === 1 ? "danger" : priority === 2 ? "warning" : "neutral"}
          />
        );
      }
      case "signal_updated_at":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {item.signal_updated_at
              ? safeFormatTimeAgo(item.signal_updated_at)
              : "—"}
          </span>
        );
      case "scheduled_at":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {["recommendation", "ai_suggestion"].includes(item._raw?.item_type)
              ? "Now"
              : formatFollowUpDate(item.scheduled_at)}
          </span>
        );
      case "reason":
      case "notes": {
        const suggestionDetail =
          columnKey === "reason" && item._raw?.item_type === "ai_suggestion"
            ? item._raw?.notes
            : null;
        return (
          <span className="inline-flex items-center gap-1">
            <span
              className="text-xs text-gray-600 dark:text-gray-300 whitespace-normal break-words"
              title={String(item[columnKey] ?? "")}
            >
              {String(item[columnKey] ?? "—")}
            </span>
            {suggestionDetail && (
              <CustomCommonTooltip content={suggestionDetail}>
                <Info
                  size={12}
                  strokeWidth={1.8}
                  className="shrink-0 cursor-pointer text-gray-400 dark:text-gray-500"
                />
              </CustomCommonTooltip>
            )}
          </span>
        );
      }
      case "last_activity_at":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {item.last_activity_at
              ? safeFormatTimeAgo(item.last_activity_at)
              : "—"}
          </span>
        );
      case "days_since_last_meaningful_engagement":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400 tabular-nums">
            {Number(item.days_since_last_meaningful_engagement) || 0} days
          </span>
        );
      case "last_meaningful_engagement_at":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {item.last_meaningful_engagement_at
              ? safeFormatTimeAgo(item.last_meaningful_engagement_at)
              : "—"}
          </span>
        );
      case "opportunity_progression":
      case "recent_buying_signal":
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
            {String(item[columnKey] || "—").toLowerCase().replace(/_/g, " ")}
          </span>
        );
      default:
        return (
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-normal break-words" title={String(item[columnKey] ?? "")}>
            {String(item[columnKey] ?? "")}
          </span>
        );
    }
  }, [createAccount, isCreatingAccount, user?.id, variant]);

  const handleRowClick = useCallback(
    (row) => {
      const raw = row._raw;
      if (variant === "similar") {
        const href = companyDomainHref(raw?.company_domain);
        if (href) window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      if (variant === "top_engaged" && !raw?.account_id) return;
      const path =
        variant === "fresh_intent"
          ? getAccountProfilePath(raw?.account ?? raw)
          : getAccountProfilePath(raw);
      if (path) {
        navigate(path);
        onClose();
      }
    },
    [variant, navigate, onClose],
  );

  return (
    <CustomModal
      isOpen={isOpen}
      width="1100px"
      className="h-[85dvh] !items-stretch p-6"
    >
      <div className="flex flex-col gap-4 w-full max-w-full min-h-0 flex-1 overflow-hidden">
        <div className="flex justify-between items-start gap-3 shrink-0">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
            <p className="text-[0.69rem] text-gray-500 dark:text-gray-400">{description}</p>
            {variant === "similar" && lastSyncedAt ? (
              <p className="text-[0.65rem] text-gray-400 dark:text-gray-500">
                Last synced: {formatLastSyncedAt(lastSyncedAt)}
              </p>
            ) : null}
            {variant === "similar" && referenceAccounts.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-2">
                <span className="text-[0.65rem] text-gray-500 dark:text-gray-400">
                  Based on:
                </span>
                {referenceAccounts.slice(0, 5).map((account, index) => (
                  <span
                    key={account?.account_id || account?.domain || `reference-${index}`}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-[0.65rem] text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    title={account?.company_name || "Reference account"}
                  >
                    <CommonAvatar
                      imageUrl={sanitizeCompanyLogoUrl(account?.company_logo)}
                      name={account?.company_name || "—"}
                      className="rounded-full object-cover w-4 h-4 shrink-0"
                      size="sm"
                    />
                    <span className="max-w-[140px] truncate">
                      {account?.company_name || "—"}
                    </span>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full shrink-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} className="text-gray-800 dark:text-gray-200" />
          </button>
        </div>
        {totalCount === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-12">
            No records to show.
          </p>
        ) : (
          <div className="flex-1 min-h-0 w-full relative">
            <CustomTable
              data={paginatedRows}
              columns={columns}
              renderCell={renderCell}
              totalCount={totalCount}
              rowsPerPage={ROWS_PER_PAGE}
              page={page}
              height="100%"
              onPageChange={setPage}
              onRowClick={handleRowClick}
              isServerSide={true}
              disableRowClickKeys={
                variant === "similar"
                  ? ["actions"]
                  : variant === "dormant"
                    ? ["open_hint"]
                    : []
              }
            />
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default AccountsDashboardSeeAllModal;
